import { QUESTION_TYPES } from '@api/instant_survey';
import { automaticallyIncludesAllCompareValues, MAX_FILTERS } from '@utilities';
import { SEARCH_PARAM_KEYS } from '../../../../../utilities/constants';

export const DISPLAY_AS = {
  COUNT: { label: 'Count', value: 'count' },
  PERCENT: { label: 'Percentages', value: 'pct' },
};

export const DISPLAY_AS_OPTIONS = [DISPLAY_AS.PERCENT, DISPLAY_AS.COUNT];

// A question's answer options live under `answers` on some question shapes and
// `options` on others. A plain `answers || options` is wrong when `answers` is
// an empty array (arrays are truthy, so it never falls through to a populated
// `options`); check length so the two sources are tried in order.
export const getQuestionOptions = (question) =>
  question?.answers?.length ? question.answers : question?.options || [];

export const getCanQuestionSort = ({ question, questionData, sortOrderByQuestion = {} }) => {
  const isTurf = questionData?.turfAnalysis != null;
  const isSortableType =
    question?.type === QUESTION_TYPES.CHECKBOX.value ||
    question?.type === QUESTION_TYPES.RADIO.value;
  const overallSeries =
    questionData?.chartPct?.series?.[0] ?? questionData?.chartCount?.series?.[0];
  const hasEnoughStandardBars = (overallSeries?.data?.length ?? 0) > 1;
  const isSequentialMonadicRadio =
    question?.type === QUESTION_TYPES.RADIO.value && question?.concepts?.length > 0;
  const conceptCategories =
    questionData?.chartPct?.xAxis?.categories ?? questionData?.chartCount?.xAxis?.categories ?? [];
  const hasEnoughMonadicBars = isSequentialMonadicRadio && conceptCategories.length > 1;

  return {
    canSortCharts: isSortableType && !isTurf && (hasEnoughStandardBars || hasEnoughMonadicBars),
    sortOrderForQuestion: sortOrderByQuestion[question?.id] ?? 'default',
  };
};

export const getQuestionsWithDisplayIndex = ({
  surveyQuestions = [],
  useQuestionnaireNumbering = false,
}) => {
  let questionIndex = 1;
  let screenerIndex = 1;

  return surveyQuestions.map((question) => {
    const isInstructionalText = question.type === QUESTION_TYPES.INSTRUCTIONS.value;
    let displayIndex;

    if (isInstructionalText) {
      displayIndex = 'T';
    } else if (useQuestionnaireNumbering && question.isScreener) {
      displayIndex = `S${screenerIndex}`;
      screenerIndex += 1;
    } else {
      displayIndex = `Q${questionIndex}`;
      questionIndex += 1;
    }

    return { ...question, displayIndex };
  });
};

export const getConceptImagesForQuestion = ({ question, quotaGroupConceptImages = [] }) => {
  if (question?.concepts?.length > 0) {
    const filteredConcepts = question.concepts
      .filter((c) => c.image?.cdnUrl)
      .map((concept) => ({
        alt: concept.name?.trim() || 'Concept',
        caption: concept.name?.trim() || 'Concept',
        id: concept.id,
        name: concept.name?.trim() || 'Concept',
        src: concept.image.cdnUrl,
      }));
    if (filteredConcepts.length > 0) {
      return filteredConcepts;
    }
  }
  if (question?.showConceptImage && quotaGroupConceptImages.length > 0) {
    return quotaGroupConceptImages;
  }
  return [];
};

export const sanitizeQuestionText = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s*\[concept_image\]\s*/gi, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Maps the `globalFilters` payload + survey questions into the GlobalFilters
 * component's `Facet[]` contract. Trip attributes are absent — the payload
 * doesn't carry them yet; the component supports them once it emits
 * `category: 'trip'` facets.
 *
 * @param {Object} args
 * @param {Object} [args.globalFilters] - transformed globalFilters payload
 * @param {string[]} [args.optionFilterableTypes] - question types that support option filtering
 * @param {Object[]} [args.surveyQuestions] - ordered survey questions
 * @return {Object[]} Facet[]
 */
export const buildGlobalFilterFacets = ({
  globalFilters,
  optionFilterableTypes = [],
  surveyQuestions = [],
}) => {
  const facets = [];

  const quotaGroups = globalFilters?.quotaGroups || [];
  if (quotaGroups.length > 1) {
    facets.push({
      category: 'quota',
      comparable: true,
      filterable: true,
      id: 'quota_group',
      label: 'Quota groups',
      options: quotaGroups.map(({ instantSurveyGroupRef, name }) => ({
        label: name,
        value: instantSurveyGroupRef,
      })),
    });
  }

  (globalFilters?.demographicFilters || []).forEach(({ compareBy, id, label, options }) => {
    facets.push({
      category: 'demographic',
      comparable: Boolean(compareBy),
      filterable: true,
      id,
      label,
      options: (options || []).map((option) => ({
        count: option.count,
        label: option.label,
        value: option.id,
      })),
    });
  });

  // `index` is the position in the full question list, so labels stay "Q{n}."
  surveyQuestions.forEach((question, index) => {
    const { id, options, title, type } = question;
    if (!optionFilterableTypes.includes(type) || !(options?.length > 0)) return;
    facets.push({
      category: 'question',
      comparable: true,
      filterable: true,
      id,
      label: `Q${index + 1}. ${title}`,
      options: options.map((option) => ({ label: option.title, value: option.id })),
    });
  });

  return facets;
};

/**
 * Drops the quota-group facet for callers that hide quota filtering entirely
 * (e.g. AskWhyResponses' `hideQuotaGroupFilters`).
 *
 * @param {Object[]} facets - Facet[]
 * @param {boolean} hideQuotaGroupFilters
 * @return {Object[]} Facet[]
 */
export const excludeQuotaGroupFacets = (facets, hideQuotaGroupFilters) =>
  hideQuotaGroupFilters ? facets.filter((facet) => facet.category !== 'quota') : facets;

const CATALOG_TYPE_TO_CATEGORY = {
  demographic: 'demographic',
  question: 'question',
  quota_group: 'quota',
  trip_attribute: 'trip',
};

/**
 * Maps the global-filters catalog response (`sections[].attributes[].values[]`)
 * into the same `Facet[]` contract as `buildGlobalFilterFacets`. The synthetic
 * `overall` attribute and any unknown type are dropped — the CompareByPicker
 * injects its own Overall entry.
 *
 * @param {Object} [catalog] - global-filters catalog response
 * @param {Object[]} [surveyQuestions] - full ordered survey question list
 * @return {Object[]} Facet[]
 */
export const buildGlobalFilterFacetsFromCatalog = (catalog, surveyQuestions = []) => {
  let questionNumber = 0;
  const questionNumbersById = new Map(
    surveyQuestions.flatMap(({ id, type }) => {
      if (type === QUESTION_TYPES.INSTRUCTIONS.value) return [];
      questionNumber += 1;
      return [[String(id), questionNumber]];
    })
  );

  return (catalog?.sections || [])
    .flatMap((section) => section?.attributes || [])
    .filter((attribute) => CATALOG_TYPE_TO_CATEGORY[attribute?.type] && attribute.id != null)
    .map((attribute) => {
      const number = questionNumbersById.get(String(attribute.id));

      return {
        category: CATALOG_TYPE_TO_CATEGORY[attribute.type],
        comparable: Boolean(attribute.compare_supported),
        filterable: Boolean(attribute.filter_supported),
        id: String(attribute.id),
        label: number ? `Q${number}. ${attribute.label}` : attribute.label,
        options: (attribute.values || []).map((option) => ({
          count: option.count,
          label: option.label,
          value: String(option.id),
        })),
      };
    });
};

const ALL_OTHERS_GROUP_NAME = 'All Others';
const COMPARE_VALUE_CAP = 10;
const QUESTION_COMPARISON_PREFIX = 'question_';
const QUESTION_FILTER_PREFIX = 'question_filter_';

/** Filter param key for a facet. Demographics and trip attributes use the bare
 * catalog id as-is (e.g. `dept`); quota groups use `quota_group`; questions use
 * `question_filter_<qid>`. */
const filterParamKey = (facet) => {
  if (facet.category === 'question') return `${QUESTION_FILTER_PREFIX}${facet.id}`;
  if (facet.category === 'quota') return 'quota_group';
  return facet.id;
};

/** Compare-by key. Questions use `question_<id>`; quota groups use the literal
 * `quota_group` the BE checks for, same as the filter key. */
const compareByKey = (facet) => {
  if (facet.category === 'question') return `${QUESTION_COMPARISON_PREFIX}${facet.id}`;
  if (facet.category === 'quota') return 'quota_group';
  return facet.id;
};

/** Question compare values are `option_<id>`-prefixed; others pass through. */
const compareValueToken = (facet, value) =>
  facet.category === 'question' ? `option_${value}` : value;

/** Reporting endpoints expect multi-value filter params to use `;`. */
const joinValues = (values) => values.join(';');

const readValues = (searchParams, key, delimiter = ';') => [
  ...new Set((searchParams.get(key) || '').split(delimiter).filter(Boolean)),
];

// Demographics predate the redesigned filters and existing report links use commas.
// Accept semicolons too so links generated before this fix continue to work.
const readFilterValues = (searchParams, facet) =>
  readValues(searchParams, filterParamKey(facet), facet.category === 'demographic' ? /[;,]/ : ';');

const validFacetValues = (facet, values) => {
  const options = new Set((facet.options || []).map(({ value }) => String(value)));
  return values.filter((value) => options.has(value));
};

const selectedCompareValues = (facet, values = []) =>
  automaticallyIncludesAllCompareValues(facet)
    ? facet.options.map(({ value }) => String(value))
    : values.slice(0, COMPARE_VALUE_CAP);

/**
 * Hydrates the controlled GlobalFilters selection from the readable URL contract.
 * Catalog flags and option ids are authoritative: unsupported or stale entries are ignored.
 *
 * @param {URLSearchParams|string} searchParams
 * @param {Object[]} facets
 * @return {Object} GlobalFilterSelection
 */
export const buildGlobalFilterSelection = (searchParams, facets = []) => {
  const params =
    searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);

  const rows = facets
    .filter(({ filterable }) => filterable)
    .map((facet) => ({
      facetId: facet.id,
      values: validFacetValues(facet, readFilterValues(params, facet)),
    }))
    .filter(({ values }) => values.length > 0)
    .slice(0, MAX_FILTERS);

  const rowOperator = params.get(SEARCH_PARAM_KEYS.ROW_OPERATOR);
  const logic = rowOperator === 'OR' ? 'OR' : 'AND';
  const comparisonAttribute = params.get(SEARCH_PARAM_KEYS.COMPARISON_ATTRIBUTE);
  const comparisonFacet = facets.find(
    (facet) => facet.comparable && compareByKey(facet) === comparisonAttribute
  );

  let compareBy = null;
  if (comparisonFacet) {
    const comparisonValues = readValues(params, SEARCH_PARAM_KEYS.COMPARISON_VALUES)
      .map((value) => {
        if (comparisonFacet.category !== 'question') return value;
        return value.startsWith('option_') ? value.slice('option_'.length) : null;
      })
      .filter(Boolean);

    const selectedValues = selectedCompareValues(
      comparisonFacet,
      validFacetValues(comparisonFacet, comparisonValues)
    );
    if (selectedValues.length) {
      compareBy = {
        facetId: comparisonFacet.id,
        includeAllOthers:
          !automaticallyIncludesAllCompareValues(comparisonFacet) &&
          params.get(SEARCH_PARAM_KEYS.COMPARISON_INCLUDE_ALL_OTHERS) === 'true',
        selectedValues,
      };
    }
  }

  return { compareBy, logic, rows };
};

/**
 * Serializes a `GlobalFilterSelection` into reporting-endpoint GET query params.
 * Returns a flat `{ key: value }` map of RAW (not URL-encoded) values — callers
 * pass this straight into the normal Axios `params` request path, which encodes
 * once via its own serializer.
 *
 * `row_operator` is always sent when any filter param is present, since the BE
 * defaults a missing value to OR.
 *
 * @param {Object} [selection] - GlobalFilterSelection ({ rows, logic, compareBy })
 * @param {Object[]} [facets] - the Facet[] the selection references (for category lookup)
 * @return {Object} flat query-param map
 */
export const buildReportingParams = (selection, facets = []) => {
  const { compareBy, logic, rows = [] } = selection || {};
  const facetsById = new Map(facets.map((facet) => [facet.id, facet]));
  const params = {};

  // Filters — one param per row; values OR'd within a key.
  rows.slice(0, MAX_FILTERS).forEach((row) => {
    const facet = facetsById.get(row.facetId);
    if (!facet || !(row.values?.length > 0)) return;
    params[filterParamKey(facet)] = joinValues(row.values);
  });

  // The BE defaults a missing row_operator to OR, so send it explicitly whenever filters apply.
  if (Object.keys(params).length > 0) params.row_operator = logic === 'OR' ? 'OR' : 'AND';

  // Overall, or a cut with nothing picked yet, emits no comparison params.
  if (compareBy) {
    const facet = facetsById.get(compareBy.facetId);
    if (facet) {
      const selectedValues = selectedCompareValues(facet, compareBy.selectedValues);
      if (selectedValues.length) {
        params.comparison_attribute = compareByKey(facet);
        params.comparison_values = selectedValues
          .map((value) => compareValueToken(facet, value))
          .join(';');
        params.comparison_include_all_others =
          !automaticallyIncludesAllCompareValues(facet) && compareBy.includeAllOthers
            ? 'true'
            : 'false';
      }
    }
  }

  return params;
};

/** Replaces the current catalog's filter params while preserving unrelated URL state. */
export const updateGlobalFilterSearchParams = (searchParams, selection, facets = []) => {
  const params = new URLSearchParams(searchParams);

  facets
    .filter(({ filterable }) => filterable)
    .forEach((facet) => params.delete(filterParamKey(facet)));
  [
    SEARCH_PARAM_KEYS.COMPARISON_ATTRIBUTE,
    SEARCH_PARAM_KEYS.COMPARISON_INCLUDE_ALL_OTHERS,
    SEARCH_PARAM_KEYS.COMPARISON_VALUES,
    SEARCH_PARAM_KEYS.ROW_OPERATOR,
  ].forEach((key) => params.delete(key));

  const urlParams = buildReportingParams(selection, facets);
  const facetsById = new Map(facets.map((facet) => [facet.id, facet]));

  // Keep the established report-link contract for demographics. New redesigned
  // filter types and reporting API requests continue to use semicolons.
  (selection?.rows || []).slice(0, MAX_FILTERS).forEach((row) => {
    const facet = facetsById.get(row.facetId);
    if (facet?.category === 'demographic' && row.values?.length) {
      urlParams[filterParamKey(facet)] = row.values.join(',');
    }
  });

  Object.entries(urlParams).forEach(([key, value]) => {
    params.set(key, value);
  });

  return params;
};

/**
 * Keeps the reporting query and the comparison metadata consumed by charts,
 * summaries, and exports derived from the same redesign selection.
 *
 * @param {Object} [selection] - GlobalFilterSelection
 * @param {Object[]} [facets] - Facet[]
 * @return {{query: Object, selectedCompareBy: Object|null}}
 */
export const buildReportingState = (selection, facets = []) => {
  const query = buildReportingParams(selection, facets);
  const compareBy = selection?.compareBy;
  const facet = facets.find(({ id }) => id === compareBy?.facetId);

  if (!facet || !query.comparison_attribute) {
    return { query, selectedCompareBy: null };
  }

  const optionsByValue = new Map(
    facet.options.map(({ label, value }) => [String(value), String(label)])
  );
  const selectedValues = selectedCompareValues(facet, compareBy.selectedValues);
  const selectedGroups = selectedValues.map((value) => ({
    id: String(value),
    name: optionsByValue.get(String(value)) || String(value),
  }));
  const groups =
    !automaticallyIncludesAllCompareValues(facet) && compareBy.includeAllOthers
      ? [...selectedGroups, { id: ALL_OTHERS_GROUP_NAME, name: ALL_OTHERS_GROUP_NAME }]
      : selectedGroups;

  return {
    query,
    selectedCompareBy: {
      category: facet.category,
      groups,
      label: facet.label,
      value: query.comparison_attribute,
    },
  };
};
